import { Box, Card } from "@mui/material";
import React, { useMemo, useRef } from "react";
import Chart from 'react-apexcharts';
import FormatToIntCurrency from "utils/FormatToIntCurrency";
import { useChartWidth } from "./useChartWidth";

const toSafeNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
};

const sanitizeSeries = (values) => (values || []).map(toSafeNumber);

const clampHeight = (height, fallback = 320) => {
    const num = Number(height);
    if (!Number.isFinite(num) || num <= 0) return fallback;
    return Math.min(Math.max(num, 250), 420);
};

export default function ColumnChart({ invested, worth, categories, height = 320 }) {
    const containerRef = useRef(null);
    const chartWidth = useChartWidth(containerRef);
    const boxHeight = clampHeight(height);
    const plotHeight = Math.max(boxHeight - 40, 200);

    const safeInvested = useMemo(() => sanitizeSeries(invested), [invested]);
    const safeWorth = useMemo(() => sanitizeSeries(worth), [worth]);
    const safeCategories = categories || [];

    const hasData = safeInvested.length > 0 || safeWorth.length > 0;
    const canRenderChart = hasData && chartWidth > 0 && plotHeight > 0;

    const series = useMemo(() => ([
        { name: 'Invested', data: safeInvested.length ? safeInvested : [0] },
        { name: 'Worth', data: safeWorth.length ? safeWorth : [0] },
    ]), [safeInvested, safeWorth]);

    const options = useMemo(() => ({
        chart: {
            sparkline: { enabled: false },
            type: 'bar',
            foreColor: '#fff',
            toolbar: { show: false },
            animations: { enabled: false },
        },
        legend: {
            show: true,
            position: 'bottom',
            labels: { colors: '#fff' },
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '55%',
                endingShape: 'rounded',
            },
        },
        dataLabels: { enabled: false },
        stroke: {
            show: true,
            width: 2,
            colors: ['transparent'],
        },
        xaxis: {
            labels: { show: false },
            categories: safeCategories,
        },
        yaxis: {
            labels: {
                formatter: (val) => FormatToIntCurrency(val),
            },
        },
        fill: { opacity: 1 },
        tooltip: {
            y: {
                min: 0,
                formatter: (val) => "$" + val.toFixed(0) + " ",
            },
        },
        grid: {
            padding: { top: 0, right: 4, bottom: 0, left: 4 },
        },
    }), [safeCategories]);

    return (
        <Box sx={{
            height: boxHeight,
            minHeight: boxHeight,
            maxHeight: boxHeight,
            width: '100%',
            overflow: 'hidden',
        }}>
            <Card
                elevation={0}
                sx={{
                    overflow: 'hidden',
                    bgcolor: 'primary.light',
                    height: '100%',
                    width: '100%',
                    padding: 1,
                    boxShadow: '0px 0px 2px #fff',
                    boxSizing: 'border-box',
                }}
            >
                <Box ref={containerRef} sx={{ width: '100%', height: plotHeight, overflow: 'hidden' }}>
                    {canRenderChart ? (
                        <Chart
                            width={chartWidth}
                            height={plotHeight}
                            options={options}
                            series={series}
                            type="bar"
                        />
                    ) : null}
                </Box>
            </Card>
        </Box>
    );
}
